/**
 * filters.ts — the charm-library filter panel.
 *
 * Structure + animations mirror the theme's collection.liquid filters: a slide-in
 * panel (clip-path) over a blur overlay, accordion groups (grid-template-rows
 * 0fr↔1fr) with a plus→minus icon morph, a header count, and a Clear-all / Show-N
 * footer. The FILTERING is client-side here — option toggles update a FacetSelection
 * and re-run filterCharms() over the catalog (facets come from data.ts buildFacets).
 */

import {
  filterCharms, sizeCode,
  type Catalog, type CharmCatalogItem, type Facet, type FacetSelection,
} from './data';
import { colorStyle } from './colors';

const el = (tag: string, cls?: string) => { const e = document.createElement(tag); if (cls) e.className = cls; return e; };

// Facet display order + labels (reference: Size, Theme, Waterproof, Material, Color).
const FACET_ORDER = ['size', 'theme', 'waterproof', 'original', 'material', 'color'];
const FACET_LABEL: Record<string, string> = {
  size: 'Size', theme: 'Theme', waterproof: 'Waterproof', original: 'PaoPao Original', material: 'Material', color: 'Color',
};
// cm bands shown next to each size code (matches the design).
const SIZE_CM_LABEL: Record<string, string> = {
  XS: '< 1 cm', S: '1 – 1.5 cm', M: '1.5 – 2 cm', L: '2 – 4 cm', XL: '> 6 cm',
};
const CLOSE_SVG = `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M5 5l14 14M19 5L5 19"/></svg>`;
const DROPLET = `<svg viewBox="0 0 16 16" width="16" height="16" fill="none"><rect width="16" height="16" rx="2" fill="#232323"/><path d="M10.156 4.488C8.95 3.06 8 2.69 8 2.69s-.95.37-2.156 1.798C4.555 5.968 3.875 7.528 3.875 9a4.125 4.125 0 1 0 8.25 0c0-1.472-.68-3.032-1.969-4.512Z" fill="#C8D7EA"/></svg>`;
// HTML plus that morphs to a minus when the group is open (see scoped CSS).
const PLUS = `<span class="cfg__plus relative block w-3.5 h-3.5">` +
  `<span class="cfg__plus-h absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[1.5px] bg-foreground"></span>` +
  `<span class="cfg__plus-v absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-[1.5px] bg-foreground"></span>` +
`</span>`;

const CHIP_BASE = 'cfg__f-chip cursor-pointer inline-flex items-center gap-1.5 rounded-xs p transition-colors';

export interface FilterApi {
  button: HTMLButtonElement;
}

export interface MountFiltersOpts {
  catalog: Catalog;
  /** where the fixed overlay + panel are appended (the configurator wrap) */
  mount: HTMLElement;
  /** called with the filtered charm list whenever the selection changes */
  onChange: (filtered: CharmCatalogItem[]) => void;
}

export function mountFilters({ catalog, mount, onChange }: MountFiltersOpts): FilterApi {
  const selection: FacetSelection = {};
  const facets = [...catalog.facets].sort(
    (a, b) => FACET_ORDER.indexOf(a.key) - FACET_ORDER.indexOf(b.key),
  );

  // ── Open button (sits in the charms-tab top bar) ──────────────────────────
  const button = el('button',
    'cfg__f-btn bg-detail/10 text-foreground rounded-xs flex items-center gap-x-2 px-2 pt-2.25 pb-2 md:px-3 leading-none transition-colors duration-250 hover:bg-detail/20 cursor-pointer') as HTMLButtonElement;
  button.type = 'button';
  const btnCount = el('span', 'cfg__f-btn-count h4 px-1.5 pt-0.75 pb-0.5 bg-[#801431] text-white rounded-xs hidden');
  const btnLabel = el('span', 'h4'); btnLabel.textContent = 'Filter';
  button.append(btnLabel, btnCount);

  // ── Overlay + panel ───────────────────────────────────────────────────────
  const overlay = el('div', 'cfg__f-overlay fixed inset-0 z-95 pointer-events-none');
  const panel = el('div',
    'cfg__f-panel fixed top-2 right-2 bottom-2 z-999 bg-background flex flex-col w-[min(92vw,440px)] rounded-sm overflow-hidden');
  panel.style.clipPath = 'inset(0 0 0 100%)';
  panel.style.pointerEvents = 'none';
  panel.setAttribute('aria-hidden', 'true');

  // Header
  const head = el('div', 'flex items-center justify-between px-4 py-4 shrink-0');
  const headLeft = el('div', 'flex items-end gap-2');
  const title = el('span', 'h3'); title.textContent = 'Filter charms';
  const headCount = el('span', 'cfg__f-head-count meta px-1.5 py-0.5 rounded-xs bg-[#801431] text-white hidden');
  headLeft.append(title, headCount);
  const closeBtn = el('button', 'p-1 -mr-1 cursor-pointer') as HTMLButtonElement;
  closeBtn.type = 'button'; closeBtn.setAttribute('aria-label', 'Close filter'); closeBtn.innerHTML = CLOSE_SVG;
  head.append(headLeft, closeBtn);

  // Body (groups)
  const body = el('div', 'flex-1 overflow-y-auto px-4');
  facets.forEach((facet, i) => body.appendChild(buildGroup(facet, i === 0))); // only the first open

  // Footer
  const footer = el('div', 'flex items-center gap-2 px-4 py-4 shrink-0');
  const clearBtn = el('button',
    'cfg__f-clear label flex-1 text-center border border-foreground/30 py-2 rounded-xs transition-colors hover:bg-foreground/5 cursor-pointer') as HTMLButtonElement;
  clearBtn.type = 'button'; clearBtn.textContent = 'Clear all';
  const showBtn = el('button',
    'label flex-1 text-center bg-foreground text-background py-2 rounded-xs transition-opacity hover:opacity-80 cursor-pointer') as HTMLButtonElement;
  showBtn.type = 'button';
  footer.append(clearBtn, showBtn);

  panel.append(head, body, footer);
  mount.append(overlay, panel);

  // ── Open / close (clip-path + overlay, like collection.liquid) ────────────
  function open(): void {
    panel.style.clipPath = 'inset(0 0 0 0%)';
    panel.style.pointerEvents = 'auto';
    panel.setAttribute('aria-hidden', 'false');
    overlay.classList.add('is-open');
  }
  function close(): void {
    panel.style.clipPath = 'inset(0 0 0 100%)';
    panel.style.pointerEvents = 'none';
    panel.setAttribute('aria-hidden', 'true');
    overlay.classList.remove('is-open');
  }
  button.addEventListener('click', open);
  closeBtn.addEventListener('click', close);
  overlay.addEventListener('click', close);
  document.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });

  // ── Selection helpers ─────────────────────────────────────────────────────
  function toggleValue(key: string, value: string): void {
    const cur = selection[key] ?? [];
    selection[key] = cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value];
    if (selection[key].length === 0) delete selection[key];
    apply();
  }
  function setSingle(key: string, value: string | null): void {
    if (value === null) delete selection[key];
    else selection[key] = [value];
    apply();
  }

  function apply(): void {
    const filtered = filterCharms(catalog.charms, selection);
    const count = Object.values(selection).reduce((n, vals) => n + vals.length, 0);

    btnCount.textContent = String(count);
    btnCount.classList.toggle('hidden', count === 0);
    headCount.textContent = String(count);
    headCount.classList.toggle('hidden', count === 0);
    showBtn.textContent = `Show ${filtered.length} result${filtered.length === 1 ? '' : 's'}`;
    clearBtn.classList.toggle('opacity-30', count === 0);
    clearBtn.classList.toggle('pointer-events-none', count === 0);

    // refresh active visuals across all controls
    panel.querySelectorAll<HTMLElement>('[data-fkey]').forEach(refreshControl);
    onChange(filtered);
  }

  clearBtn.addEventListener('click', () => { for (const k of Object.keys(selection)) delete selection[k]; apply(); close(); });
  showBtn.addEventListener('click', close);

  // first paint
  apply();

  return { button };

  // ── Group / option builders ─────────────────────────────────────────────
  function buildGroup(facet: Facet, openByDefault: boolean): HTMLElement {
    const group = el('div', 'cfg__f-group border-b border-foreground/10');
    const toggle = el('button', 'w-full flex items-center justify-between gap-4 py-3.5 cursor-pointer') as HTMLButtonElement;
    toggle.type = 'button';
    const labelWrap = el('span', 'h4 flex items-center gap-1.5');
    if (facet.key === 'waterproof') labelWrap.innerHTML = `<span class="inline-flex w-4 h-4">${DROPLET}</span>`;
    else if (facet.key === 'original' && catalog.originalBadgeIcon) labelWrap.innerHTML = `<span class="inline-flex w-4 h-4 items-center justify-center rounded-[3px] bg-foreground"><img class="w-3 h-3 object-contain" src="${catalog.originalBadgeIcon}" alt=""></span>`;
    labelWrap.insertAdjacentText('beforeend', FACET_LABEL[facet.key] ?? facet.label);
    const dot = el('span', 'cfg__f-dot w-1.5 h-1.5 rounded-full shrink-0 bg-[#801431] hidden');
    labelWrap.appendChild(dot);
    const iconWrap = el('span', 'shrink-0 flex items-center justify-center w-5 h-5');
    iconWrap.innerHTML = PLUS;
    toggle.append(labelWrap, iconWrap);

    const gbody = el('div', 'cfg__f-group-body');
    const inner = el('div', 'min-h-0 overflow-hidden');
    inner.appendChild(buildOptions(facet));
    const spacer = el('div', 'pb-5'); // bottom breathing room when open
    inner.appendChild(spacer);
    gbody.appendChild(inner);

    group.append(toggle, gbody);
    group.classList.toggle('cfg__f-group--open', openByDefault); // accordion: only one open
    toggle.setAttribute('aria-expanded', String(openByDefault));
    // Accordion behaviour: opening a group closes the others.
    toggle.addEventListener('click', () => {
      const willOpen = !group.classList.contains('cfg__f-group--open');
      body.querySelectorAll('.cfg__f-group').forEach(g => {
        g.classList.remove('cfg__f-group--open');
        g.querySelector('button')?.setAttribute('aria-expanded', 'false');
      });
      group.classList.toggle('cfg__f-group--open', willOpen);
      toggle.setAttribute('aria-expanded', String(willOpen));
    });
    // mark the group's dot for active-state refresh
    (dot as HTMLElement).dataset.fkey = facet.key;
    return group;
  }

  function buildOptions(facet: Facet): HTMLElement {
    if (facet.key === 'waterproof') return buildSegmented('waterproof', [
      { label: 'Yes', val: 'Waterproof' }, { label: 'No', val: 'Not waterproof' }, { label: 'Both', val: null },
    ]);
    if (facet.key === 'original') return buildSegmented('original', [
      { label: 'Yes', val: 'Original' }, { label: 'No', val: 'Not original' }, { label: 'Both', val: null },
    ]);
    const wrap = el('div', 'flex flex-wrap gap-1.5');
    for (const v of facet.values) {
      const label = el('label', `${CHIP_BASE} bg-detail/10 hover:bg-detail/20 px-2.5 py-1.75`);
      label.dataset.fkey = facet.key;
      label.dataset.fval = v.value;
      const input = el('input') as HTMLInputElement;
      input.type = 'checkbox'; input.className = 'sr-only';
      input.addEventListener('change', () => toggleValue(facet.key, v.value));
      label.appendChild(input);

      if (facet.key === 'size') {
        const code = sizeCode(v.value);
        const badge = el('span', 'cfg__f-size-badge w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold bg-foreground text-background transition-colors');
        badge.textContent = code;
        label.appendChild(badge);
        label.insertAdjacentText('beforeend', SIZE_CM_LABEL[code] ?? v.value);
        label.classList.add('pl-1'); // tighter on the badge side
      } else if (facet.key === 'color') {
        const sw = el('span', 'w-3.5 h-3.5 rounded-full border border-foreground/20 shrink-0');
        sw.setAttribute('style', colorStyle(v.value));
        label.appendChild(sw);
        label.insertAdjacentText('beforeend', v.value);
      } else {
        label.insertAdjacentText('beforeend', v.value);
      }
      wrap.appendChild(label);
    }
    return wrap;
  }

  // Segmented Yes / No / Both with a sliding indicator (collection bool-toggle).
  function buildSegmented(key: string, opts: { label: string; val: string | null }[]): HTMLElement {
    const wrap = el('div', 'cfg__f-seg relative flex bg-detail/10 p-1 rounded-sm w-full');
    wrap.dataset.fkey = key;
    const indicator = el('span', 'cfg__f-seg-ind absolute top-1 bottom-1 left-1 rounded-xs bg-detail/30 transition-transform duration-200 ease-out pointer-events-none');
    indicator.style.width = `calc((100% - 0.5rem) / ${opts.length})`;
    indicator.style.transform = `translateX(${(opts.length - 1) * 100}%)`; // default → Both
    wrap.appendChild(indicator);
    opts.forEach((o, i) => {
      const b = el('button', 'cfg__f-seg-btn relative z-1 w-full text-center px-3 py-1.5 rounded-xs p transition-colors') as HTMLButtonElement;
      b.type = 'button'; b.textContent = o.label; b.dataset.fval = o.val ?? '';
      b.addEventListener('click', () => { setSingle(key, o.val); indicator.style.transform = `translateX(${i * 100}%)`; });
      wrap.appendChild(b);
    });
    return wrap;
  }

  // Reflect the current selection on a control (chips, size badges, segmented, dots).
  function refreshControl(node: HTMLElement): void {
    const key = node.dataset.fkey!;
    const active = selection[key] ?? [];

    if (node.classList.contains('cfg__f-dot')) {
      node.classList.toggle('hidden', active.length === 0);
      return;
    }
    if (node.classList.contains('cfg__f-seg')) {
      node.querySelectorAll<HTMLElement>('.cfg__f-seg-btn').forEach(b => {
        const on = (active[0] ?? '') === (b.dataset.fval ?? '');
        b.classList.toggle('text-foreground', on);
        b.classList.toggle('text-foreground/60', !on);
      });
      return;
    }
    // chip <label>
    const val = node.dataset.fval!;
    const on = active.includes(val);
    node.classList.toggle('bg-detail/10', !on);
    node.classList.toggle('bg-detail/30', on);
    const badge = node.querySelector('.cfg__f-size-badge');
    if (badge) {
      badge.classList.toggle('bg-foreground', !on);
      badge.classList.toggle('text-background', !on);
      badge.classList.toggle('bg-[#801431]', on);
      badge.classList.toggle('text-white', on);
    }
  }
}
